/**
 * Site (v0.1.166, specs/site-istatistikleri.md): the last 30 days of cadgaragepro.com on one page, the same numbers
 * as the weekly Telegram summary. On top the state of the four sources (server log, Google Analytics, Search
 * Console, Bing Webmaster), then the key figures, a daily bar chart (the API kullanımı chart's way: inline SVG, a
 * title and a hidden table read the numbers), where people came from, the AI assistants, the crawlers as they call
 * themselves, the pages, GA4, Google and Bing searches. A source without its key says so and links to its card.
 *
 * Plan items 2 and 13 (SEO and AI plan, 25 Oct 2026): a table of the pages the AI services opened live for someone's
 * question or read for their index, with the arrivals from ChatGPT and the Etsy clicks made on each page by where the
 * visitor came from; and the wanted scans of the last 90 days (vehicle, part, how often, whether we sell it).
 *
 * Bot gözü testi (26 Oct 2026): the last weekly run of the important pages as the crawlers and a browser saw them,
 * page by page and crawler by crawler (status and first byte, or what went wrong), and the Markdown copies' state.
 */

import { SITE_SOURCE_GROUP_LABELS, type SiteSourceGroup } from '@twin/contracts';
import { type ReactNode, useEffect } from 'react';
import type { ConnectionStatus } from '../api/settings.js';
import {
  SITE_BOT_EYE_AGENTS,
  SITE_BOT_EYE_PAGE_KIND_LABELS,
  SITE_BOT_EYE_PROBLEM_LABELS,
  SITE_CLICK_SOURCE_LABELS,
  SITE_CRAWLER_GROUP_LABELS,
  SITE_CRAWLER_LABELS,
  SITE_OTHER_PATH,
  SITE_REFERRER_LABELS,
  SITE_SOURCE_LABELS,
  SITE_SOURCE_STATUS_LABELS,
  SITE_WANTED_AREA_LABELS,
  SITE_WANTED_CATALOG_LABELS,
  SITE_WANTED_SOURCE_LABELS,
  type SiteAiAgentCounts,
  type SiteAiPage,
  type SiteAiTotals,
  type SiteBotEyeAgent,
  type SiteBotEyeCheck,
  type SiteBotEyeRun,
  type SiteClickSource,
  type SiteCount,
  type SiteCrawler,
  type SiteCrawlerCheck,
  type SiteCrawlerGroup,
  type SiteCrawlerRangeState,
  type SiteDay,
  type SiteReferrer,
  type SiteSearchSummary,
  type SiteSource,
  type SiteSourceState,
  type SiteSourceStatus,
  type SiteStatsView,
  type SiteWanted,
  type SiteWantedArea,
  type SiteWantedSource,
} from '../api/site-stats.js';
import { usePanel } from '../app/context.js';
import { useIsDesktop } from '../app/media.js';
import { Link } from '../app/router.js';
import { useSiteStats } from '../app/site-stats.js';
import { formatIsoDay, formatNumber, formatRelativeClock, istanbulIsoDate } from '../lib/format.js';
import { BarValueLabels, ChartFrame, ChartReadout, pickHandlers, useChartPick } from './chart-kit.js';
import { Icon } from './icons.js';
import { StatusGlyph } from './settings.js';
import { DaySummary, SourceDailyChart } from './site-sources-chart.js';

/** The connection pill shape each state borrows (Ayarlar › Bağlantılar). */
const STATUS_SHAPE: Readonly<Record<SiteSourceStatus, ConnectionStatus>> = Object.freeze({
  ok: 'connected',
  not_connected: 'not_connected',
  error: 'error',
  no_data: 'awaiting_sercan',
  waiting: 'applying',
});

/** Where a source's key is entered; the server log needs none. */
const SOURCE_CONNECTION: Readonly<Record<SiteSource, string | null>> = Object.freeze({
  server_log: null,
  ga4: 'ga4',
  search_console: 'ga4',
  bing: 'bing-webmaster',
});

function StatusChip({ status }: { status: SiteSourceStatus }) {
  const shape = STATUS_SHAPE[status];
  return (
    <span className={`cpill cpill--${shape} au-chip`}>
      <StatusGlyph status={shape} inPill />
      {SITE_SOURCE_STATUS_LABELS[status]}
    </span>
  );
}

function n(value: number): string {
  return formatNumber(value);
}

function pct(value: number | null): string {
  return value === null ? '-' : `%${formatNumber(value * 100, 1)}`;
}

function SourceRow({ source, now }: { source: SiteSourceState; now: number }) {
  const connection = SOURCE_CONNECTION[source.id];
  const headingId = `sx-src-${source.id}`;
  const needsKey = source.status === 'not_connected' || source.status === 'error' || source.status === 'no_data';
  return (
    <li className="sx-source">
      <div className="sx-source__head">
        <h3 id={headingId} className="sx-source__name">
          {SITE_SOURCE_LABELS[source.id]}
        </h3>
        <StatusChip status={source.status} />
      </div>
      {source.detailTr === null ? null : <p className="sx-source__detail">{source.detailTr}</p>}
      <p className="sx-source__meta">
        {source.checkedAt === null
          ? 'Henüz okunmadı'
          : `Son okuma ${formatRelativeClock(Date.parse(source.checkedAt), now)}`}
        {source.dataThrough === null ? null : ` · veri ${formatIsoDay(source.dataThrough)} gününe kadar`}
      </p>
      {connection !== null && needsKey ? (
        <Link to={`/ayarlar/baglanti/${connection}`} className="sx-source__link">
          Ayarlar › Bağlantılar
          <Icon name="chevron-right" size={14} />
        </Link>
      ) : null}
    </li>
  );
}

/**
 * The 30 days as bars: page views in the quiet colour, entries from outside in the strong data colour on top of them
 * (never the accent, which means "needs you"). The busiest and the last day carry their entries; a tap, a click or the
 * arrow keys pick a day for the readout row (audit chart rules, 25 Oct 2026).
 */
export function SiteBars({ daily }: { daily: readonly SiteDay[] }) {
  const pick = useChartPick(daily.length);
  const views = daily.reduce((sum, day) => sum + day.pageViews, 0);
  if (views === 0) return <p className="au-bars__empty">Son 30 günde insan sayfa görüntülemesi yok.</p>;
  const width = 300;
  const height = 64;
  const gap = 2;
  const max = Math.max(1, ...daily.map((day) => day.pageViews));
  const bar = (width - gap * (daily.length - 1)) / Math.max(1, daily.length);
  const entries = daily.reduce((sum, day) => sum + day.entries, 0);
  const peak = daily.reduce<SiteDay | null>(
    (best, day) => (best === null || day.pageViews > best.pageViews ? day : best),
    null,
  );
  const first = daily[0]?.day ?? '';
  const last = daily.at(-1)?.day ?? '';
  const titleId = 'sx-bars-title';
  const title = `Son 30 gün: ${n(entries)} giriş, ${n(views)} sayfa görüntüleme; en çok ${formatIsoDay(peak?.day ?? '')} (${n(peak?.pageViews ?? 0)} sayfa)`;
  const shown = daily[pick.index];
  const readout =
    shown === undefined
      ? ''
      : `${formatIsoDay(shown.day)}: ${n(shown.entries)} giriş · ${n(shown.pageViews)} sayfa · ${n(shown.crawlerHits)} bot isteği`;
  return (
    <figure className="au-bars sx-bars">
      <ChartFrame label="Site, son 30 gün: gün seç" count={daily.length} pick={pick} valueText={readout}>
        <div className="chart-plot">
          <svg
            className="au-bars__svg sx-bars__svg"
            viewBox={`0 0 ${width} ${height}`}
            preserveAspectRatio="none"
            role="img"
            aria-labelledby={titleId}
          >
            <title id={titleId}>{title}</title>
            <line className="au-bars__base" x1={0} y1={height - 0.5} x2={width} y2={height - 0.5} />
            {daily.map((day, index) => {
              const x = index * (bar + gap);
              const all = (day.pageViews / max) * (height - 2);
              const outside = (Math.min(day.entries, day.pageViews) / max) * (height - 2);
              return (
                <g key={day.day} className={pick.index === index ? 'is-picked' : undefined}>
                  <rect
                    className="chart-hit"
                    x={x}
                    y={0}
                    width={bar + gap}
                    height={height}
                    {...pickHandlers(pick, index)}
                  />
                  {all > 0 ? (
                    <rect
                      className="sx-bars__views"
                      x={x}
                      y={height - 1 - all}
                      width={bar}
                      height={all}
                      pointerEvents="none"
                    />
                  ) : null}
                  {outside > 0 ? (
                    <rect
                      className="sx-bars__entries"
                      x={x}
                      y={height - 1 - outside}
                      width={bar}
                      height={outside}
                      pointerEvents="none"
                    />
                  ) : null}
                </g>
              );
            })}
          </svg>
          <BarValueLabels values={daily.map((day) => day.pageViews)} max={max} text={n} />
        </div>
      </ChartFrame>
      <ChartReadout>{readout}</ChartReadout>
      <DaySummary day={shown} />
      <figcaption className="au-bars__cap" aria-hidden="true">
        <span>{formatIsoDay(first)}</span>
        <span>
          <span className="sx-key sx-key--entries" /> giriş · <span className="sx-key sx-key--views" /> sayfa
        </span>
        <span>{formatIsoDay(last)}</span>
      </figcaption>
      {/* A table ignores the 1 px width of .sr-only; the wrapper clips it so the page cannot scroll sideways. */}
      <div className="sr-only">
        <table>
          <caption>Site: son 30 gün, gün gün</caption>
          <thead>
            <tr>
              <th scope="col">Gün</th>
              <th scope="col">Giriş</th>
              <th scope="col">Sayfa görüntüleme</th>
              <th scope="col">Yapay zekâdan giriş</th>
              <th scope="col">Bot isteği</th>
            </tr>
          </thead>
          <tbody>
            {daily.map((day) => (
              <tr key={day.day}>
                <th scope="row">{formatIsoDay(day.day)}</th>
                <td>{day.entries}</td>
                <td>{day.pageViews}</td>
                <td>{day.aiEntries}</td>
                <td>{day.crawlerHits}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}

function CountList({
  label,
  rows,
  name,
}: {
  label: string;
  rows: readonly SiteCount[];
  name: (key: string) => string;
}) {
  if (rows.length === 0) return <p className="sx-empty">Yok.</p>;
  return (
    <ol className="sx-counts" aria-label={label}>
      {rows.map((row) => (
        <li key={row.key} className="sx-counts__row">
          <span className="sx-counts__key">{name(row.key)}</span>
          <span className="sx-counts__value mono">{n(row.count)}</span>
        </li>
      ))}
    </ol>
  );
}

function pageName(key: string): string {
  return key === SITE_OTHER_PATH ? 'diğer sayfalar' : key;
}

function Card({ id, title, note, children }: { id: string; title: string; note?: string; children: ReactNode }) {
  const headingId = `sx-${id}-h`;
  return (
    <article className="au-card sx-card" aria-labelledby={headingId}>
      <header className="au-card__head">
        <div className="au-card__titles">
          <h3 id={headingId} className="au-card__name">
            {title}
          </h3>
          {note === undefined ? null : <p className="au-card__purpose">{note}</p>}
        </div>
      </header>
      {children}
    </article>
  );
}

/** Live opens for a person's question: ChatGPT-User, Perplexity-User, Claude-User. */
function liveOf(row: SiteAiAgentCounts): number {
  return row.chatgptUser + row.perplexityUser + row.claudeUser;
}

/** Search index reads: OAI-SearchBot, PerplexityBot, Claude-SearchBot. */
function indexOf(row: SiteAiAgentCounts): number {
  return row.oaiSearchbot + row.perplexitybot + row.claudeSearchbot;
}

function joined(values: readonly number[]): string {
  return values.map((value) => n(value)).join(' / ');
}

/**
 * 26 Oct 2026: a figure of the AI table with its address verdict: the verified count, and under it the fetches checked
 * and not in the crawler's published list ("listede yok") and the ones never checked ("denetlenmeyen": counted before
 * the verdicts or before the list's first read), apart, as the Botlar table shows them. An answer without the split
 * says both in one figure; one without verdicts shows the claimed count as before.
 */
function Verified({
  claimed,
  verified,
  unverified,
}: {
  claimed: number[];
  verified: number[] | null;
  unverified: number[] | null;
}) {
  if (verified === null) return <>{joined(claimed)}</>;
  const missed = claimed.map((value, index) => Math.max(0, value - (verified[index] ?? 0)));
  if (unverified === null) {
    return (
      <>
        {joined(verified)}
        {missed.some((value) => value > 0) ? (
          <span className="sx-ai__src">doğrulanmayan ya da denetlenmeyen {joined(missed)}</span>
        ) : null}
      </>
    );
  }
  const outside = missed.map((value, index) => Math.min(value, unverified[index] ?? 0));
  const unchecked = missed.map((value, index) => value - (outside[index] ?? 0));
  return (
    <>
      {joined(verified)}
      {outside.some((value) => value > 0) ? <span className="sx-ai__src">listede yok {joined(outside)}</span> : null}
      {unchecked.some((value) => value > 0) ? (
        <span className="sx-ai__src">denetlenmeyen {joined(unchecked)}</span>
      ) : null}
    </>
  );
}

function agentCells(
  page: SiteAiAgentCounts,
  verified: SiteAiAgentCounts | undefined,
  unverified: SiteAiAgentCounts | undefined,
  keys: readonly AiAgentKey[],
) {
  return {
    claimed: keys.map((key) => page[key]),
    verified: verified === undefined ? null : keys.map((key) => verified[key]),
    unverified: verified === undefined || unverified === undefined ? null : keys.map((key) => unverified[key]),
  };
}

type AiAgentKey = keyof SiteAiAgentCounts;

function clickSourceName(key: string): string {
  return SITE_CLICK_SOURCE_LABELS[key as SiteClickSource] ?? key;
}

function countsText(rows: readonly SiteCount[], name: (key: string) => string, limit: number): string {
  return rows
    .slice(0, limit)
    .map((row) => `${name(row.key)} ${n(row.count)}`)
    .join(', ');
}

/** Plan item 2: per page, what the AI services did with it and the Etsy clicks made on it. */
function AiPagesCard({
  pages,
  totals,
  verified,
  unverified,
  clickSources,
}: {
  pages: readonly SiteAiPage[];
  totals: SiteAiTotals | undefined;
  verified: SiteAiAgentCounts | undefined;
  unverified: SiteAiAgentCounts | undefined;
  clickSources: readonly SiteCount[];
}) {
  return (
    <Card
      id="aipages"
      title="Yapay zekâ, sayfa sayfa"
      note={
        verified === undefined
          ? 'Canlı açılış: bir kişinin sorusu için sayfayı o an açan kullanıcı botları. Dizin: yapay zekâ aramalarının dizin botları. Adları sütunlarda; kendini böyle tanıtan, doğrulanmaz.'
          : 'Canlı açılış: bir kişinin sorusu için sayfayı o an açan kullanıcı botları. Dizin: yapay zekâ aramalarının dizin botları. Adları sütunlarda. Sayılar adresi botun yayımlanan listesinde olan istekler; altında adresi listede olmayanlar ve denetlenmeyenler (doğrulama başlamadan ya da liste ilk kez okunmadan önce gelenler).'
      }
    >
      {totals === undefined ? (
        <p className="sx-empty">Henüz sayılmadı.</p>
      ) : (
        <dl className="au-card__nums sx-nums">
          <div className="au-num">
            <dt>Canlı açılış</dt>
            <dd className="mono">
              <Verified
                claimed={[liveOf(totals)]}
                verified={verified === undefined ? null : [liveOf(verified)]}
                unverified={verified === undefined || unverified === undefined ? null : [liveOf(unverified)]}
              />
            </dd>
          </div>
          <div className="au-num">
            <dt>Dizin okuması</dt>
            <dd className="mono">
              <Verified
                claimed={[indexOf(totals)]}
                verified={verified === undefined ? null : [indexOf(verified)]}
                unverified={verified === undefined || unverified === undefined ? null : [indexOf(unverified)]}
              />
            </dd>
          </div>
          <div className="au-num">
            <dt>ChatGPT'den giriş</dt>
            <dd className="mono">{n(totals.chatgptEntries)}</dd>
          </div>
        </dl>
      )}
      {pages.length === 0 ? (
        <p className="sx-empty">Son 30 günde yapay zekâ okuması ya da sayfadan Etsy tıklaması yok.</p>
      ) : (
        <div className="sx-scroll">
          <table className="sx-table sx-ai">
            <caption>Sayfalar, en çok kişi işlemi olan önce</caption>
            <thead>
              <tr>
                <th scope="col">Sayfa</th>
                {/* The crawlers as their User-Agent names them, as in the Botlar card (SITE_CRAWLER_LABELS). */}
                <th scope="col">{SITE_CRAWLER_LABELS.chatgpt_user}</th>
                <th scope="col">{SITE_CRAWLER_LABELS.oai_searchbot}</th>
                <th scope="col">
                  {SITE_CRAWLER_LABELS.perplexity_user} / {SITE_CRAWLER_LABELS.perplexitybot}
                </th>
                <th scope="col">
                  {SITE_CRAWLER_LABELS.claude_user} / {SITE_CRAWLER_LABELS.claude_searchbot}
                </th>
                <th scope="col">ChatGPT'den giriş</th>
                <th scope="col">Etsy tıklaması</th>
              </tr>
            </thead>
            <tbody>
              {pages.map((page) => (
                <tr key={page.path}>
                  <th scope="row" className="mono">
                    {page.path}
                    {page.file === true ? <span className="sx-ai__src">dosya, toplamlarda yok</span> : null}
                  </th>
                  <td className="mono">
                    <Verified {...agentCells(page, page.verified, page.unverified, ['chatgptUser'])} />
                  </td>
                  <td className="mono">
                    <Verified {...agentCells(page, page.verified, page.unverified, ['oaiSearchbot'])} />
                  </td>
                  <td className="mono">
                    <Verified
                      {...agentCells(page, page.verified, page.unverified, ['perplexityUser', 'perplexitybot'])}
                    />
                  </td>
                  <td className="mono">
                    <Verified
                      {...agentCells(page, page.verified, page.unverified, ['claudeUser', 'claudeSearchbot'])}
                    />
                  </td>
                  <td className="mono">{n(page.chatgptEntries)}</td>
                  <td className="mono">
                    {n(page.etsyClicks)}
                    {page.etsyBySource.length === 0 ? null : (
                      <span className="sx-ai__src">{countsText(page.etsyBySource, clickSourceName, 3)}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {clickSources.length === 0 ? null : (
        <>
          <h4 className="key ink-2 sx-sub">Etsy linkine tıklayanlar, geldikleri yere göre</h4>
          <CountList label="Etsy linkine tıklayanlar, kaynağa göre" rows={clickSources} name={clickSourceName} />
          <p className="au-card__note">
            Sunucu kaydı: aynı ziyaretçinin aynı saatlik okumada, aynı kayıt dosyasındaki son dış girişi. Bu sürümden
            beri sayılır.
          </p>
        </>
      )}
    </Card>
  );
}

/** Plan item 13: the vehicles people asked for, most first. */
function WantedCard({ wanted }: { wanted: SiteWanted | undefined }) {
  return (
    <Card
      id="wanted"
      title="İstenen taramalar"
      note="Son 90 gün: site asistanının ekibe devrettiği sorular, site formları, cevaplanamayan sorular, özel iş talepleri. Araç ve bölge sayılır, soru metni gösterilmez."
    >
      {wanted === undefined ? (
        <p className="sx-empty">Henüz okunmadı.</p>
      ) : wanted.rows.length === 0 ? (
        <p className="sx-empty">
          {wanted.requests === 0
            ? 'Son 90 günde istek yok.'
            : `Son 90 günde ${n(wanted.requests)} istek var; hiçbirinde araç anlaşılmadı.`}
        </p>
      ) : (
        <>
          <p className="au-card__note">
            {n(wanted.requests)} istek, {n(wanted.rows.length)} araç
            {wanted.unmatched > 0 ? `; ${n(wanted.unmatched)} istekte araç anlaşılmadı` : ''}.
          </p>
          <div className="sx-scroll">
            <table className="sx-table sx-wanted">
              <caption>
                İstenen araçlar, {formatIsoDay(wanted.from)} - {formatIsoDay(wanted.to)}
              </caption>
              <thead>
                <tr>
                  <th scope="col">Araç</th>
                  <th scope="col">İstek</th>
                  <th scope="col">Bölge</th>
                  <th scope="col">Kaynak</th>
                  <th scope="col">Durum</th>
                </tr>
              </thead>
              <tbody>
                {wanted.rows.map((row) => (
                  <tr key={row.vehicle}>
                    <th scope="row">{row.vehicle}</th>
                    <td className="mono">
                      {n(row.requests)}
                      {row.recent > 0 ? <span className="sx-ai__src">bu hafta {n(row.recent)}</span> : null}
                    </td>
                    <td className="sx-wanted__text">
                      {countsText(row.areas, (key) => SITE_WANTED_AREA_LABELS[key as SiteWantedArea] ?? key, 3)}
                    </td>
                    <td className="sx-wanted__text">
                      {countsText(row.sources, (key) => SITE_WANTED_SOURCE_LABELS[key as SiteWantedSource] ?? key, 3)}
                    </td>
                    <td className="sx-wanted__text">
                      {SITE_WANTED_CATALOG_LABELS[row.catalog]}
                      {row.listings !== undefined && row.listings.length > 0 ? ` (${row.listings.join(', ')})` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  );
}

/** The published range lists' state in one sentence. */
function rangesNote(ranges: readonly SiteCrawlerRangeState[]): string {
  const read = ranges.filter((range) => range.fetchedAt !== null);
  const failing = ranges.filter((range) => range.error !== null).length;
  const oldest = read
    .map((range) => Date.parse(range.fetchedAt ?? ''))
    .filter((at) => Number.isFinite(at))
    .sort((a, b) => a - b)[0];
  const since = oldest === undefined ? '' : `, en eskisi ${formatIsoDay(istanbulIsoDate(oldest))}`;
  const failed = failing > 0 ? `; ${n(failing)} listenin son okuması düştü, eski kopyası kullanılıyor` : '';
  return `Yayımlanan adres listeleri: ${n(ranges.length)} listeden ${n(read.length)} okundu${since}${failed}. Liste yayımlamayan bot doğrulanamaz.`;
}

/**
 * 26 Oct 2026: each named crawler's hits of the 30 days by address verdict (its logged /24 or /48 against the ranges
 * its operator publishes), and the lists' last reads.
 */
function CrawlerChecks({
  checks,
  ranges,
}: {
  checks: readonly SiteCrawlerCheck[] | undefined;
  ranges: readonly SiteCrawlerRangeState[] | undefined;
}) {
  if (checks === undefined) return null;
  return (
    <>
      <h4 className="key ink-2 sx-sub">Adres doğrulaması</h4>
      {checks.length === 0 ? (
        <p className="sx-empty">Son 30 günde adı bilinen bot isteği yok.</p>
      ) : (
        <div className="sx-scroll">
          <table className="sx-table sx-checks">
            <caption>Botlar, adres doğrulamasına göre</caption>
            <thead>
              <tr>
                <th scope="col">Bot</th>
                <th scope="col">Kendini tanıtan</th>
                <th scope="col">Doğrulanan</th>
                <th scope="col">Listede yok</th>
                <th scope="col">Denetlenmeyen</th>
              </tr>
            </thead>
            <tbody>
              {checks.map((row) => (
                <tr key={row.key}>
                  <th scope="row">{SITE_CRAWLER_LABELS[row.key as SiteCrawler] ?? row.key}</th>
                  <td className="mono">{n(row.claimed)}</td>
                  <td className="mono">{n(row.verified)}</td>
                  <td className="mono">{n(row.unverified)}</td>
                  <td className="mono">
                    {n(row.unchecked + row.noList)}
                    {row.noList > 0 ? <span className="sx-ai__src">liste yayımlamıyor</span> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {ranges === undefined ? null : <p className="au-card__note">{rangesNote(ranges)}</p>}
    </>
  );
}

/** A crawler by the name its User-Agent gives (SITE_CRAWLER_LABELS), the browser in Turkish. */
function botEyeAgentName(agent: SiteBotEyeAgent): string {
  return agent === 'browser' ? 'Tarayıcı' : SITE_CRAWLER_LABELS[agent];
}

function seconds(ms: number): string {
  return `${formatNumber(ms / 1000, 1)} sn`;
}

/**
 * The browser's own product or vehicle page without its price or measured block: a finding of the page (some real
 * pages have none), not a failure; the crawlers are judged against it.
 */
function pageKeyNote(check: SiteBotEyeCheck): string | null {
  if (check.agent !== 'browser' || check.variant !== 'html' || check.hasKey !== false) return null;
  if (check.problems.includes('no_key')) return null;
  if (check.kind === 'vehicle') return 'sayfada ölçü bloğu yok';
  if (check.kind === 'product') return 'sayfada fiyat yok';
  return null;
}

/** One cell of the run's table: the status and first byte, or what went wrong. */
function BotEyeCell({ check }: { check: SiteBotEyeCheck | undefined }) {
  if (check === undefined) return <td className="mono ink-3">-</td>;
  const answer = check.status === null ? '' : String(check.status);
  const time = check.ttfbMs === null ? '' : seconds(check.ttfbMs);
  const notes = check.problems.map((problem) => SITE_BOT_EYE_PROBLEM_LABELS[problem]);
  const pageNote = pageKeyNote(check);
  return (
    <td className={check.ok && notes.length === 0 ? 'mono' : 'mono sx-bot__bad'}>
      {[answer, time].filter((part) => part !== '').join(' · ') || '-'}
      {check.variant === 'markdown' && check.noindex === true ? <span className="sx-ai__src">noindex</span> : null}
      {notes.length === 0 ? null : <span className="sx-ai__src">{notes.join('; ')}</span>}
      {pageNote === null ? null : <span className="sx-ai__src">{pageNote}</span>}
    </td>
  );
}

/** Bot gözü testi: the last weekly run of the important pages as the crawlers and a browser saw them. */
function BotEyeCard({ run, now }: { run: SiteBotEyeRun | null | undefined; now: number }) {
  const note =
    "Her pazartesi 07:00: ana sayfa, en çok açılan ürün, araç ve rehber sayfaları ve robots.txt, sitemap.xml, llms.txt botların ve bir tarayıcının gözüyle açılır. Sorun Telegram'a uyarı olur.";
  if (run === undefined || run === null) {
    return (
      <Card id="boteye" title="Bot gözü testi" note={note}>
        <p className="sx-empty">{run === null ? 'Henüz çalışmadı.' : 'Henüz okunmadı.'}</p>
      </Card>
    );
  }
  const html = run.checks.filter((check) => check.variant === 'html');
  const paths = [...new Set(html.map((check) => check.path))];
  const markdownLive = run.markdown === 'live';
  const slowest = Math.max(0, ...run.checks.map((check) => check.ttfbMs ?? 0));
  const found = run.checks.filter((check) => check.problems.length > 0).length;
  return (
    <Card id="boteye" title="Bot gözü testi" note={note}>
      <dl className="au-card__nums sx-nums">
        <div className="au-num">
          <dt>Sayfa x tarayıcı</dt>
          <dd className="mono">
            {n(run.pages)} x {n(run.agents)}
          </dd>
        </div>
        <div className="au-num">
          <dt>Açılmayan</dt>
          <dd className="mono">{n(run.failed)}</dd>
        </div>
        <div className="au-num">
          <dt>İlk bayt, en çok</dt>
          <dd className="mono">{seconds(slowest)}</dd>
        </div>
      </dl>
      <p className="au-card__note">
        {`Son test ${formatRelativeClock(Date.parse(run.startedAt), now)}, ${n(run.requests)} istek`}
        {found > run.failed ? `; ${n(found - run.failed)} kontrolde dikkat edilecek bulgu` : ''}.{' '}
        {markdownLive
          ? 'Markdown kopyaları canlı: Accept: text/markdown cevabı ve noindex başlığı her hafta denetlenir.'
          : 'Markdown kopyaları henüz yayında değil; denetim yayına girince kendiliğinden başlar.'}
        {run.stopped ? ' İstek sınırına gelince durdu, kalan sayfalar denenmedi.' : ''}
      </p>
      <div className="sx-scroll">
        <table className="sx-table sx-bot">
          <caption>Sayfalar ve tarayıcılar: cevap ve ilk bayt</caption>
          <thead>
            <tr>
              <th scope="col">Sayfa</th>
              {SITE_BOT_EYE_AGENTS.map((agent) => (
                <th scope="col" key={agent}>
                  {botEyeAgentName(agent)}
                </th>
              ))}
              {markdownLive ? <th scope="col">Markdown</th> : null}
            </tr>
          </thead>
          <tbody>
            {paths.map((path) => {
              const here = run.checks.filter((check) => check.path === path);
              const kind = here[0]?.kind;
              return (
                <tr key={path}>
                  <th scope="row" className="mono">
                    {path}
                    {kind === undefined ? null : (
                      <span className="sx-ai__src">{SITE_BOT_EYE_PAGE_KIND_LABELS[kind]}</span>
                    )}
                  </th>
                  {SITE_BOT_EYE_AGENTS.map((agent) => (
                    <BotEyeCell
                      key={agent}
                      check={here.find((check) => check.agent === agent && check.variant === 'html')}
                    />
                  ))}
                  {markdownLive ? <BotEyeCell check={here.find((check) => check.variant === 'markdown')} /> : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function sourceOf(view: SiteStatsView, id: SiteSource): SiteSourceState | undefined {
  return view.sources.find((source) => source.id === id);
}

/** What a source without numbers says in its card: its state's sentence. */
function Missing({ source }: { source: SiteSourceState | undefined }) {
  if (source === undefined) return <p className="sx-empty">Henüz okunmadı.</p>;
  return (
    <p className="sx-empty">
      {SITE_SOURCE_STATUS_LABELS[source.status]}
      {source.detailTr === null ? '.' : `: ${source.detailTr}`}
    </p>
  );
}

function SearchCard({
  id,
  title,
  note,
  summary,
  source,
}: {
  id: string;
  title: string;
  note: string;
  summary: SiteSearchSummary | null;
  source: SiteSourceState | undefined;
}) {
  return (
    <Card id={id} title={title} note={note}>
      {summary === null ? (
        <Missing source={source} />
      ) : (
        <>
          <dl className="au-card__nums sx-nums">
            <div className="au-num">
              <dt>Gösterim</dt>
              <dd className="mono">{n(summary.impressions)}</dd>
            </div>
            <div className="au-num">
              <dt>Tıklama</dt>
              <dd className="mono">
                {n(summary.clicks)} <span className="ink-3">({pct(summary.ctr)})</span>
              </dd>
            </div>
            <div className="au-num">
              <dt>Ortalama sıra</dt>
              <dd className="mono">{summary.position === null ? '-' : formatNumber(summary.position, 1)}</dd>
            </div>
          </dl>
          <p className="au-card__note">
            {summary.dataThrough === null
              ? 'Veri günü bilinmiyor.'
              : `Veri ${formatIsoDay(summary.dataThrough)} gününe kadar`}
            {summary.partial ? '; son günler henüz kesin değil.' : '.'}
          </p>
          {summary.topQueries.length === 0 ? null : (
            <table className="sx-table">
              <caption>{title}: en çok gösterilen sorgular</caption>
              <thead>
                <tr>
                  <th scope="col">Sorgu</th>
                  <th scope="col">Gösterim</th>
                  <th scope="col">Tıklama</th>
                  <th scope="col">Sıra</th>
                </tr>
              </thead>
              <tbody>
                {summary.topQueries.map((row) => (
                  <tr key={row.key}>
                    <th scope="row">{row.key}</th>
                    <td className="mono">{n(row.impressions)}</td>
                    <td className="mono">{n(row.clicks)}</td>
                    <td className="mono">{row.position === null ? '-' : formatNumber(row.position, 1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {summary.topPages.length === 0 ? null : (
            <>
              <p className="sx-sublabel" aria-hidden="true">
                En çok gösterilen sayfalar (gösterim)
              </p>
              <CountList
                label={`${title}: en çok gösterilen sayfalar`}
                rows={summary.topPages.map((row) => ({ key: row.key, count: row.impressions }))}
                name={(key) => key.replace(/^https:\/\/(www\.)?cadgaragepro\.com/, '') || '/'}
              />
            </>
          )}
          {source?.status === 'error' && source.detailTr !== null ? (
            <p className="au-card__error">{source.detailTr}</p>
          ) : null}
        </>
      )}
    </Card>
  );
}

function SiteSummary({
  view,
  loading,
  error,
  onReload,
}: {
  view: SiteStatsView;
  loading: boolean;
  error: string | null;
  onReload: () => void;
}) {
  const clock = usePanel().now;
  const log = view.log;
  return (
    <section className="au-sum drafting" aria-labelledby="sx-sum-h">
      <h2 id="sx-sum-h" className="au-sum__title">
        Son 30 gün: <span className="mono">{n(log.entries)}</span> giriş
      </h2>
      <dl className="au-sum__grid">
        <div>
          <dt>Sayfa görüntüleme</dt>
          <dd className="mono">{n(log.pageViews)}</dd>
        </div>
        <div>
          <dt>Yapay zekâdan giriş</dt>
          <dd className="mono">{n(log.aiEntries)}</dd>
        </div>
        <div>
          <dt>Bot isteği</dt>
          <dd className="mono">{n(log.crawlerHits)}</dd>
        </div>
        <div>
          <dt>GA4 oturum</dt>
          <dd className="mono">{view.ga4 === null ? '-' : n(view.ga4.sessions)}</dd>
        </div>
        <div>
          <dt>Etsy linki (sunucu)</dt>
          <dd className="mono">{n(log.buyClickTotal ?? 0)}</dd>
        </div>
        <div>
          <dt>Etsy düğmesi (GA4)</dt>
          <dd className="mono">{view.ga4 === null ? '-' : n(view.ga4.etsyClicks)}</dd>
        </div>
        <div>
          <dt>Google gösterim</dt>
          <dd className="mono">{view.google === null ? '-' : n(view.google.impressions)}</dd>
        </div>
      </dl>
      {/* 28 Oct 2026: 42 server clicks against 29 in GA4; the same reason the weekly report gives. */}
      {view.ga4 !== null && (log.buyClickTotal ?? 0) > view.ga4.etsyClicks ? (
        <p className="au-card__note">
          Etsy tıklamasını sunucu kaydı GA4'ten fazla sayar: reklam engelleyicisi olan ya da çerez iznini reddeden
          ziyaretçinin tıklaması GA4'e düşmez. Sunucu kaydı yalnız tarayıcı gibi davranan ziyaretçileri sayar, botları
          saymaz.
        </p>
      ) : null}
      <SiteBars daily={view.daily} />
      <p className="au-sum__meta">
        <span>
          {formatIsoDay(view.from)} - {formatIsoDay(view.to)} · güncellendi{' '}
          {formatRelativeClock(Date.parse(view.generatedAt), clock)}
        </span>
        <button type="button" className="btn btn--secondary btn--small" onClick={onReload} disabled={loading}>
          <Icon name="refresh-cw" size={14} />
          {loading ? 'Yenileniyor…' : 'Yenile'}
        </button>
      </p>
      {error === null ? null : (
        <p className="au-sum__error" role="status">
          Yenilenemedi: {error}
        </p>
      )}
    </section>
  );
}

export function SitePage() {
  const { store, state } = useSiteStats();
  const desktop = useIsDesktop();
  useEffect(() => {
    void store.reload();
  }, [store]);
  const view = state.view;
  if (view === null) {
    return state.error === null ? (
      <div className="loading" role="status">
        Yükleniyor…
      </div>
    ) : (
      <div className="shop-status" role="status">
        Site istatistikleri yüklenemedi: {state.error}{' '}
        <button type="button" className="btn btn--secondary" onClick={() => void store.reload()}>
          Tekrar dene
        </button>
      </div>
    );
  }
  const now = Date.parse(view.generatedAt);
  const log = view.log;
  const ga4 = view.ga4;
  return (
    <div className={desktop ? 'au au--desk sx' : 'au sx'}>
      <SiteSummary view={view} loading={state.loading} error={state.error} onReload={() => void store.reload()} />
      <section aria-labelledby="sx-daily-h" className="sx-section">
        <h2 id="sx-daily-h" className="sx-section__title">
          Günlük ziyaretçi, kaynağa göre
        </h2>
        <SourceDailyChart daily={view.daily} />
        <p className="au-card__note">
          Google Analytics oturumları. Avrupa'dan çerez izni vermeyenler burada görünmez; sunucu kaydındaki giriş sayısı
          bu yüzden daha yüksektir.
        </p>
      </section>
      <section aria-labelledby="sx-sources-h" className="sx-section">
        <h2 id="sx-sources-h" className="sx-section__title">
          Kaynaklar
        </h2>
        <ul className="sx-sources">
          {view.sources.map((source) => (
            <SourceRow key={source.id} source={source} now={now} />
          ))}
        </ul>
      </section>
      <ul className="au-list sx-list" aria-label="Site rakamları">
        <li>
          <Card id="ref" title="Nereden geldiler" note="Siteye dışarıdan girişler, sunucu kaydı">
            <CountList
              label="Giriş kaynakları"
              rows={log.referrers}
              name={(key) => SITE_REFERRER_LABELS[key as SiteReferrer] ?? key}
            />
          </Card>
        </li>
        <li>
          <Card
            id="ai"
            title="Yapay zekâ asistanları"
            note="ChatGPT, Perplexity, Claude, Gemini, Copilot'tan gelen girişler"
          >
            <CountList
              label="Asistanlardan girişler"
              rows={log.assistants}
              name={(key) => SITE_REFERRER_LABELS[key as SiteReferrer] ?? key}
            />
          </Card>
        </li>
        <li className="sx-li--wide">
          <AiPagesCard
            pages={log.aiPages ?? []}
            totals={log.aiTotals}
            verified={log.aiVerified}
            unverified={log.aiUnverified}
            clickSources={log.buyClickSources ?? []}
          />
        </li>
        <li>
          <Card
            id="bots"
            title="Botlar"
            note="Kendini böyle tanıtan (User-Agent); adresi, yayımlanan listesi olan botlarda ayrıca doğrulanır"
          >
            <CountList
              label="Bot grupları"
              rows={log.crawlers}
              name={(key) => SITE_CRAWLER_GROUP_LABELS[key as SiteCrawlerGroup] ?? key}
            />
            {log.crawlerAgents.length === 0 ? null : (
              <p className="au-card__note">
                {log.crawlerAgents
                  .slice(0, 8)
                  .map((row) => `${SITE_CRAWLER_LABELS[row.key as SiteCrawler] ?? row.key} ${n(row.count)}`)
                  .join(' · ')}
              </p>
            )}
            <CrawlerChecks checks={log.crawlerChecks} ranges={view.crawlerRanges} />
          </Card>
        </li>
        <li className="sx-li--wide">
          <BotEyeCard run={view.botEye} now={now} />
        </li>
        <li>
          <Card id="pages" title="En çok açılan sayfalar" note="İnsanların sayfa görüntülemesi, sunucu kaydı">
            <CountList label="Sayfalar" rows={log.topPages} name={pageName} />
          </Card>
        </li>
        <li>
          <Card id="ga4" title="Google Analytics" note="Oturum, kanal ve Etsy düğmesi tıklamaları">
            {ga4 === null ? (
              <Missing source={sourceOf(view, 'ga4')} />
            ) : (
              <>
                <dl className="au-card__nums sx-nums">
                  <div className="au-num">
                    <dt>Oturum</dt>
                    <dd className="mono">{n(ga4.sessions)}</dd>
                  </div>
                  <div className="au-num">
                    <dt>Kişi (günlük toplam)</dt>
                    <dd className="mono">{n(ga4.users)}</dd>
                  </div>
                  <div className="au-num">
                    <dt>Etsy düğmesi</dt>
                    <dd className="mono">{n(ga4.etsyClicks)}</dd>
                  </div>
                </dl>
                <CountList
                  label="Kanallar"
                  rows={ga4.channels.map((row) => ({ key: row.key, count: row.sessions }))}
                  name={(key) => key}
                />
                {ga4.etsyClickSources.length === 0 ? null : (
                  <>
                    <h4 className="key ink-2 sx-sub">Etsy'ye geçenler, geldikleri yere göre</h4>
                    <CountList
                      label="Etsy'ye geçenler, kaynağa göre"
                      rows={ga4.etsyClickSources}
                      name={(key) => SITE_SOURCE_GROUP_LABELS[key as SiteSourceGroup] ?? key}
                    />
                  </>
                )}
                {ga4.etsyClickPages.length === 0 ? null : (
                  <p className="au-card__note">
                    Etsy'ye en çok tıklatan:{' '}
                    {ga4.etsyClickPages
                      .slice(0, 3)
                      .map((row) => `${row.key} (${n(row.count)})`)
                      .join(', ')}
                  </p>
                )}
              </>
            )}
          </Card>
        </li>
        <li>
          <SearchCard
            id="google"
            title="Google aramaları"
            note="Search Console; günler Pasifik saatine göre, 2-3 gün geriden gelir"
            summary={view.google}
            source={sourceOf(view, 'search_console')}
          />
        </li>
        <li>
          <SearchCard
            id="bing"
            title="Bing (Copilot dahil)"
            note="Bing Webmaster; sorgu listesi haftalık"
            summary={view.bing}
            source={sourceOf(view, 'bing')}
          />
        </li>
        <li className="sx-li--wide">
          <WantedCard wanted={view.wanted} />
        </li>
      </ul>
      <p className="au-foot">
        Sunucu kaydı günleri {view.timeZone} saatine göredir; giriş, yönlendireni site olmayan sayfa görüntülemesidir
        (ziyaretçi kimliği tutulmaz). Botlar kendi beyanlarına göre sayılır; yayımlanan adres listesi olanların adresi
        ayrıca doğrulanır.
      </p>
    </div>
  );
}
